import { FunctionComponent } from "react";
import { useParams } from "react-router-dom";

import { Stack, Text } from "@bps/fluent-ui";
import {
  BpIdUserDto,
  SignInTypeKeys
} from "@libs/api/gateways/bp-id/bp-id.dtos";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { RepairFederatedUserAccountsButton } from "./RepairFederatedUserAccountsButton";
import { RepairFederatedUserButton } from "./RepairFederatedUserButton";

export interface UserBpIdIdentitesProps {
  bpUserDetails: BpIdUserDto;
}
export const UserBpIdIdentites: FunctionComponent<UserBpIdIdentitesProps> = ({
  bpUserDetails
}) => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const identitiesForThisTenant = bpUserDetails?.signInIdentities.filter(x =>
    x.issuerAssignedId.startsWith(`PR\\Bp Premier Online\\${tenantId}`)
  );

  const { data: hasUserWritePermission } = usePermissionsCheckQuery(
    Permissions.PltBpidUserWrite
  );

  const needsRepair =
    hasUserWritePermission && identitiesForThisTenant?.length > 1;

  // Added 2026-09-30. A separate malformation from the multi-tenant
  // duplicate case above: this account still carries a local emailAddress sign-in
  // identity alongside its federated one, instead of having it folded into
  // otherMails (see UserIdentityServiceAgent.RepairFederatedUserAsync). B2C's
  // native federated lookup can resolve this single account via both identities
  // independently within one sign-in attempt, surfacing it twice in its built-in
  // "select an account" disambiguation screen even though there's only one real
  // account. Confirmed via a live trace + Graph query during investigation.
  const hasFederatedIdentity = bpUserDetails?.signInIdentities.some(
    x => x.signInType === SignInTypeKeys.federated
  );

  const hasEmailIdentity = bpUserDetails?.signInIdentities.some(
    x => x.signInType === SignInTypeKeys.email
  );

  const needsEmailFederatedRepair =
    hasUserWritePermission && hasFederatedIdentity && hasEmailIdentity;

  const Repair: FunctionComponent = () => (
    <Stack tokens={{ childrenGap: 8 }}>
      <Text>
        <b>Warning </b>
      </Text>
      <Text>
        This account has nore than one federated identity assigned for this
        tenant, which may cause login issues.
      </Text>
      <Text>Click 'repair' below to fix this issue.</Text>

      <Stack horizontal tokens={{ childrenGap: 8 }}>
        {needsRepair && (
          <RepairFederatedUserAccountsButton bpIdUserId={bpUserDetails.id} />
        )}
      </Stack>
    </Stack>
  );

  const EmailFederatedRepair: FunctionComponent = () => (
    <Stack tokens={{ childrenGap: 8 }}>
      <Text>
        <b>Warning </b>
      </Text>
      <Text>
        This account has a local email sign-in identity alongside its federated
        identity, which can cause B2C to show a duplicate-looking "select an
        account" prompt during federated sign-in.
      </Text>
      <Text>Click 'repair' below to fix this issue.</Text>

      <Stack horizontal tokens={{ childrenGap: 8 }}>
        {needsEmailFederatedRepair && (
          <RepairFederatedUserButton bpIdUserId={bpUserDetails.id} />
        )}
      </Stack>
    </Stack>
  );

  if (!needsRepair && !needsEmailFederatedRepair) {
    return null;
  }

  return (
    <Stack tokens={{ childrenGap: 8 }}>
      {needsRepair && <Repair />}
      {needsEmailFederatedRepair && <EmailFederatedRepair />}
    </Stack>
  );
};
